import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListOfficialDailyRequest } from '../../model/base/request/list-official-daily-request';
import { useOfficialDailyController } from './context/official-daily-controller';
import { useOfficialDailyState } from './context/official-daily-context';
import { useNavigate, useParams } from 'react-router-dom';

export function OfficialDailyComponent() {
  const navigate = useNavigate();
  const { year } = useParams<{ year: string }>();
  const { loadData } = useOfficialDailyController();
  const { officialDaily, loading } = useOfficialDailyState();
  const [currentPage, setCurrentPage] = useState(1);

  // Calcula datas inicial e final do ano se year está presente
  const getYearDateRange = (yearParam?: string) => {
    if (!yearParam) return { startDate: '', endDate: '' };

    const yearNum = parseInt(yearParam, 10);
    if (isNaN(yearNum)) return { startDate: '', endDate: '' };

    return {
      startDate: `${yearNum}-01-01`,
      endDate: `${yearNum}-12-31`,
    };
  };

  const yearDateRange = getYearDateRange(year);

  const [filters, setFilters] = useState<IListOfficialDailyRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    startDate: yearDateRange.startDate,
    endDate: yearDateRange.endDate,
  });

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por número da edição ou descrição',
      fullWidth: false,
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
    },
  ];

  useEffect(() => {
    // Atualizar filtros se o year mudar
    if (year) {
      const yearDateRange = getYearDateRange(year);
      const updatedFilters = {
        ...filters,
        startDate: yearDateRange.startDate,
        endDate: yearDateRange.endDate,
        page: 0,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      loadData(updatedFilters);
    } else {
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(0);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListOfficialDailyRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      startDate: '',
      endDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(0);

    // Se houver filtro de ano na URL, redirecionar para a rota sem filtros
    if (year) {
      navigate('/diario-oficial');
    } else {
      loadData(clearedFilters);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewDocument = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadDocument = (url?: string, editionNumber?: string) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `diario-oficial-edicao-${editionNumber || 'documento'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <ListPageLayout
      title={year ? `Edições do Diário Oficial - ${year}` : "Edições do Diário Oficial"}
      subtitle={year
        ? `Consulte as edições publicadas do Diário Oficial do ano de ${year}`
        : "Consulte as edições publicadas do Diário Oficial do município"
      }
      breadcrumbs={
        year
          ? [
              { label: 'Edições do Diário Oficial', href: '/diario-oficial' },
              { label: year }
            ]
          : [{ label: 'Edições do Diário Oficial' }]
      }
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListOfficialDailyRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading && !officialDaily.hasItems() ? (
        <TableSkeleton rows={12} columns={5} hasActions={true} />
      ) : !officialDaily.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma edição do Diário Oficial encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '60px',
                      }}
                    >
                      <ArticleIcon sx={{ verticalAlign: 'middle' }} />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '5%',
                      }}
                    >
                      Edição
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '5%',
                      }}
                    >
                      Ano
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Data de Publicação
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      Descrição
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '150px',
                        textAlign: 'center',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officialDaily.items.map((daily) => (
                    <TableRow
                      key={daily.id}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{
                              fontSize: 32,
                              color: 'error.main',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                          }}
                        >
                          {daily.editionNumber || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={daily.yearPublication || '-'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(daily.publicationDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                          }}
                        >
                          {daily.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title="Ver Detalhes" arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => navigate(`/diario-oficial/detalhes/${daily.id}`)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Visualizar PDF" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDocument(daily.fileViewUrl)}
                              disabled={!daily.fileViewUrl}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Baixar PDF" arrow>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleDownloadDocument(daily.fileDownloadUrl, daily.editionNumber)}
                              disabled={!daily.fileDownloadUrl}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'success.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {officialDaily.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={officialDaily.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
